import type { PlannedTouchPointResponse, TripResponse } from '../../api/routingJobsApi';
import { decodePolyline6 } from '../../routeGeometry/routeGeometry';
import { buildExecutionMetrics, orderActivitiesForDetails } from '../../components/trips/executionModel';
import { COLORS } from '../../config';
import type {
  ExecutionActivityViewModel,
  ExecutionPhase,
  ExecutionTripViewModel,
} from '../../components/trips/executionTypes';

type PreviewScenario = 'COMPLETED' | 'ACTIVE_DELAYED' | 'RTO' | 'OUT_OF_SEQUENCE' | 'UNPLANNED' | 'PENDING' | 'ACTIVE';
const PREVIEW_SCENARIOS: PreviewScenario[] = [
  'COMPLETED',
  'ACTIVE_DELAYED',
  'RTO',
  'OUT_OF_SEQUENCE',
  'UNPLANNED',
  'PENDING',
  'ACTIVE',
];

const stableHash = (value: string): number => {
  let hash = 0;
  for (let index = 0; index < value.length; index += 1) {
    hash = ((hash * 31) + value.charCodeAt(index)) >>> 0;
  }
  return hash;
};

const round = (value: number, precision = 1): number => {
  const multiplier = 10 ** precision;
  return Math.round(value * multiplier) / multiplier;
};

const minutesBetween = (start: number, end: number): number => Math.max(0, Math.round((end - start) / 60_000));

const getDepotLocation = (trip: TripResponse): [number, number] => {
  const depotNode = trip.plannedRoute.orderedNodes[0];
  return depotNode ? [depotNode.longitude, depotNode.latitude] : [77.5946, 12.9716];
};

const activityForTouchPoint = (
  trip: TripResponse,
  point: PlannedTouchPointResponse,
  index: number,
  scenario: PreviewScenario,
  completedCount: number,
  actualOrder: number[]
): ExecutionActivityViewModel => {
  const actualIndex = actualOrder.indexOf(index);
  const isVisited = actualIndex >= 0 && actualIndex < completedCount;
  const isActive = actualIndex === completedCount && scenario !== 'PENDING' && scenario !== 'COMPLETED';
  const delayBase = scenario === 'ACTIVE_DELAYED' ? 34 : scenario === 'OUT_OF_SEQUENCE' ? 18 : 4;
  const arrivalDelta = isVisited || isActive ? delayBase + actualIndex * 3 : null;
  const checkIn = arrivalDelta == null ? null : point.plannedArrivalAtEpoch + arrivalDelta * 60_000;
  const serviceMinutes = isVisited ? 11 + ((index + stableHash(trip.id)) % 9) : isActive ? 6 : null;
  const checkOut = isVisited && checkIn != null ? checkIn + (serviceMinutes ?? 0) * 60_000 : null;
  const isRto = scenario === 'RTO' && isVisited && actualIndex === Math.max(0, completedCount - 1);
  const exceptionTags: string[] = [];
  if (arrivalDelta != null && arrivalDelta >= 20) exceptionTags.push('Late arrival');
  if (scenario === 'OUT_OF_SEQUENCE' && isVisited && actualIndex !== index) exceptionTags.push('Out of sequence');
  if (isRto) exceptionTags.push('RTO');
  const plannedLegDistance = round((Number(trip.metrics.distanceKm) || Math.max(8, trip.touchPoints.length * 3.5)) / (trip.touchPoints.length + 1));
  const actualFactor = scenario === 'OUT_OF_SEQUENCE' ? 1.24 : scenario === 'ACTIVE_DELAYED' ? 1.12 : 1.04;

  return {
    id: `${trip.id}-${point.touchPointId}`,
    kind: 'TOUCH_POINT',
    title: point.buyerName || point.touchPointId,
    subtitle: point.address || point.slotTag || 'Delivery point',
    location: [point.longitude, point.latitude],
    plannedReference: point.touchPointId,
    plannedSequence: point.sequence,
    actualSequence: isVisited || isActive ? actualIndex + 1 : null,
    plannedAttemptAtEpoch: point.plannedAttemptAtEpoch,
    plannedArrivalAtEpoch: point.plannedArrivalAtEpoch,
    plannedDepartureAtEpoch: point.plannedDepartureAtEpoch,
    actualCheckInAtEpoch: checkIn,
    actualCheckOutAtEpoch: checkOut,
    serviceMinutes,
    dwellMinutes: serviceMinutes == null ? null : serviceMinutes + (isActive ? 4 : 0),
    arrivalDeltaMinutes: arrivalDelta,
    outcome: isRto ? 'RTO' : isVisited ? 'SUCCESS' : isActive ? 'IN_PROGRESS' : 'PENDING',
    exceptionTags,
    incomingTravel: {
      plannedDistanceKm: plannedLegDistance,
      actualDistanceKm: isVisited || isActive ? round(plannedLegDistance * actualFactor) : null,
      plannedMinutes: Math.max(5, minutesBetween(
        index === 0 ? trip.plannedDepartureAtEpoch : trip.touchPoints[index - 1].plannedDepartureAtEpoch,
        point.plannedArrivalAtEpoch
      )),
      actualMinutes: isVisited || isActive ? Math.max(6, Math.round(17 * actualFactor) + (arrivalDelta ?? 0)) : null,
    },
  };
};

const buildPlannedRoute = (trip: TripResponse): [number, number][][] => {
  return trip.plannedRoute.segments.flatMap((segment) => {
    if (segment.geometryPolyline6) {
      try {
        const decoded = decodePolyline6(segment.geometryPolyline6);
        if (decoded.length >= 2) return [decoded];
      } catch {
        // Invalid geometry is omitted; Track Trips does not draw synthetic connectors.
      }
    }
    return [];
  });
};

export const buildJumboBuyExecutionPreview = (
  trip: TripResponse,
  previewIndex?: number
): ExecutionTripViewModel => {
  const scenarioIndex = previewIndex == null ? stableHash(trip.id) % PREVIEW_SCENARIOS.length : previewIndex % PREVIEW_SCENARIOS.length;
  const scenario = PREVIEW_SCENARIOS[scenarioIndex];
  const deliveryCount = trip.touchPoints.length;
  const completedCount = scenario === 'COMPLETED'
    ? deliveryCount
    : scenario === 'PENDING'
      ? 0
      : Math.min(deliveryCount, Math.max(1, Math.floor(deliveryCount * (scenario === 'ACTIVE' ? 0.55 : 0.4))));
  const actualOrder = trip.touchPoints.map((_, index) => index);
  if (scenario === 'OUT_OF_SEQUENCE' && deliveryCount >= 2) {
    [actualOrder[0], actualOrder[1]] = [actualOrder[1], actualOrder[0]];
  }

  const depotLocation = getDepotLocation(trip);
  const departureActual = scenario === 'PENDING' ? null : trip.plannedDepartureAtEpoch + (scenario === 'ACTIVE_DELAYED' ? 12 : 3) * 60_000;
  const activities: ExecutionActivityViewModel[] = [{
    id: `${trip.id}-DEPOT-DEPARTURE`,
    kind: 'DEPOT_DEPARTURE',
    title: 'Depot departure',
    subtitle: trip.depotId,
    location: depotLocation,
    plannedSequence: 0,
    actualSequence: departureActual == null ? null : 0,
    plannedAttemptAtEpoch: trip.plannedDepartureAtEpoch,
    plannedArrivalAtEpoch: trip.plannedDepartureAtEpoch,
    plannedDepartureAtEpoch: trip.plannedDepartureAtEpoch,
    actualCheckInAtEpoch: departureActual,
    actualCheckOutAtEpoch: departureActual,
    serviceMinutes: 0,
    dwellMinutes: 0,
    arrivalDeltaMinutes: departureActual == null ? null : minutesBetween(trip.plannedDepartureAtEpoch, departureActual),
    outcome: departureActual == null ? 'PENDING' : 'DEPARTED',
    exceptionTags: [],
    incomingTravel: { plannedDistanceKm: null, actualDistanceKm: null, plannedMinutes: null, actualMinutes: null },
  }];

  activities.push(...trip.touchPoints.map((point, index) => (
    activityForTouchPoint(trip, point, index, scenario, completedCount, actualOrder)
  )));

  if (scenario === 'UNPLANNED' && trip.touchPoints.length) {
    const anchor = trip.touchPoints[Math.min(completedCount, trip.touchPoints.length - 1)];
    activities.push({
      id: `${trip.id}-UNPLANNED-1`,
      kind: 'UNPLANNED_STOP',
      title: 'Unplanned stop',
      subtitle: 'Preview roadside dwell',
      location: [anchor.longitude + 0.004, anchor.latitude - 0.003],
      plannedSequence: null,
      actualSequence: completedCount + 1,
      plannedAttemptAtEpoch: null,
      plannedArrivalAtEpoch: null,
      plannedDepartureAtEpoch: null,
      actualCheckInAtEpoch: anchor.plannedDepartureAtEpoch + 18 * 60_000,
      actualCheckOutAtEpoch: anchor.plannedDepartureAtEpoch + 31 * 60_000,
      serviceMinutes: 13,
      dwellMinutes: 13,
      arrivalDeltaMinutes: null,
      outcome: 'SUCCESS',
      exceptionTags: ['Unplanned stop'],
      incomingTravel: { plannedDistanceKm: null, actualDistanceKm: 1.8, plannedMinutes: null, actualMinutes: 9 },
    });
  }

  const completedReturn = scenario === 'COMPLETED';
  const lastPoint = trip.touchPoints[trip.touchPoints.length - 1];
  const returnLegDistance = round((Number(trip.metrics.distanceKm) || Math.max(8, deliveryCount * 3.5)) / (deliveryCount + 1));
  activities.push({
    id: `${trip.id}-DEPOT-RETURN`,
    kind: 'DEPOT_RETURN',
    title: 'Depot return',
    subtitle: trip.depotId,
    location: depotLocation,
    plannedSequence: deliveryCount + 1,
    actualSequence: completedReturn ? deliveryCount + 1 : null,
    plannedAttemptAtEpoch: trip.plannedReturnAtEpoch,
    plannedArrivalAtEpoch: trip.plannedReturnAtEpoch,
    plannedDepartureAtEpoch: trip.plannedReturnAtEpoch,
    actualCheckInAtEpoch: completedReturn ? trip.plannedReturnAtEpoch + 17 * 60_000 : null,
    actualCheckOutAtEpoch: completedReturn ? trip.plannedReturnAtEpoch + 17 * 60_000 : null,
    serviceMinutes: 0,
    dwellMinutes: 0,
    arrivalDeltaMinutes: completedReturn ? 17 : null,
    outcome: completedReturn ? 'RETURNED' : 'PENDING',
    exceptionTags: [],
    incomingTravel: {
      plannedDistanceKm: returnLegDistance,
      actualDistanceKm: completedReturn ? round(returnLegDistance * 1.06) : null,
      plannedMinutes: lastPoint ? minutesBetween(lastPoint.plannedDepartureAtEpoch, trip.plannedReturnAtEpoch) : null,
      actualMinutes: completedReturn ? 22 : null,
    },
  });

  const orderedActivities = orderActivitiesForDetails(activities);
  const latestActual = [...orderedActivities].reverse().find((activity) => activity.actualSequence != null) ?? null;
  const currentActivity = orderedActivities.find((activity) => activity.outcome === 'IN_PROGRESS') ?? null;
  const nextActivity = orderedActivities.find((activity) => activity.kind === 'TOUCH_POINT' && activity.outcome === 'PENDING') ?? null;
  const plannedDurationMinutes = minutesBetween(trip.plannedDepartureAtEpoch, trip.plannedReturnAtEpoch);
  const lastActualTime = Math.max(
    departureActual ?? trip.plannedDepartureAtEpoch,
    ...activities.map((activity) => activity.actualCheckOutAtEpoch ?? activity.actualCheckInAtEpoch ?? 0)
  );
  const actualDurationMinutes = departureActual == null ? 0 : minutesBetween(departureActual, lastActualTime);
  const plannedDistanceKm = Number(trip.metrics.distanceKm) || round(Math.max(8, deliveryCount * 3.5));
  const metrics = buildExecutionMetrics(activities, plannedDistanceKm, plannedDurationMinutes, actualDurationMinutes);
  const attentionReasons = Array.from(new Set(activities.flatMap((activity) => activity.exceptionTags)));
  if (scenario === 'ACTIVE_DELAYED') attentionReasons.unshift('Running behind plan');
  const phase: ExecutionPhase = scenario === 'PENDING'
    ? 'PENDING'
    : scenario === 'COMPLETED'
      ? 'COMPLETED'
      : metrics.delayMinutes >= 20
        ? 'DELAYED'
        : 'ACTIVE';
  const actualRoute = orderedActivities
    .filter((activity) => activity.actualSequence != null)
    .map((activity) => activity.location);

  return {
    id: trip.id,
    plannedTrip: trip,
    phase,
    color: COLORS.tripColors[(previewIndex ?? stableHash(trip.id)) % COLORS.tripColors.length],
    preview: true,
    updatedAtEpoch: lastActualTime || trip.updatedAtEpoch,
    currentActivityId: currentActivity?.id ?? latestActual?.id ?? null,
    currentPointLabel: currentActivity?.title ?? latestActual?.title ?? 'Not departed',
    nextPointLabel: nextActivity?.title ?? (completedReturn ? 'Complete' : 'Depot return'),
    attentionReasons,
    activities: orderedActivities,
    route: { planned: buildPlannedRoute(trip), actual: actualRoute },
    vehiclePosition: currentActivity?.location ?? latestActual?.location ?? null,
    metrics,
  };
};
